import React from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button, Steps } from "antd";
import { useTherapyWizard } from "./useTherapyWizard";

// Importamos asignando nombres PascalCase para calmar al Linter
import { Step1Identity, Step2Clinical, Step3Visibility } from "./Steps";

import "./TherapyWizard.scss";
import { useMovil } from "../../../../hooks/useIsNarrow";
import { useHistorialHoja } from "../../../../hooks/useHistorialHoja";

// Los tres pasos del asistente, en orden. La clave viaja al diccionario; el
// indice se queda aqui, que es donde manda el orden de las pantallas.
const PASOS = ["identidad", "disenoClinico", "visibilidad"];
const TOTAL_PASOS = PASOS.length;

export function TherapyWizard({ isOpen, onClose, onSuccess, initialData }) {
  const movil = useMovil();

  // El boton atras del telefono cierra el asistente en vez de sacar de la pagina.
  useHistorialHoja(movil && isOpen, onClose);

  // "Cancelar" y "Guardar cambios" ya viven en el bloque comun.
  const { t } = useTranslation(["rooms", "common"]);

  const {
    currentStep,
    formData,
    categories,
    loading,
    user,
    handleChange,
    next,
    prev,
    canProceed,
    handleSubmit,
    isEditing,
  } = useTherapyWizard({ onClose, onSuccess, initialData });

  // Renderizado condicional del contenido
  const renderContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <Step1Identity
            data={formData}
            categories={categories}
            onChange={handleChange}
          />
        );
      case 1:
        return <Step2Clinical data={formData} onChange={handleChange} />;
      case 2:
        return (
          <Step3Visibility
            data={formData}
            onChange={handleChange}
            user={user}
          />
        );
      default:
        return null;
    }
  };

  // Configuración de los pasos para Ant Design v5
  const stepsItems = [
    { key: "identity", title: "" },
    { key: "clinical", title: "" },
    { key: "visibility", title: "" },
  ];

  return (
    <Modal
      // PARCHE DE SEGURIDAD: Usamos ambas props por si acaso hay conflicto de versiones
      open={isOpen} // Ant Design v5
      visible={isOpen} // Ant Design v4 (Legacy fallback)
      onCancel={onClose}
      footer={null}
      width={800}
      centered
      // FIX: Actualizado a destroyOnHidden para evitar el warning de deprecación
      destroyOnHidden={true}
      className="revi-wizard-modal"
      rootClassName="revi-hoja"
    >
      {/* Header Personalizado */}
      <div className="wizard-header">
        {/*
          El titulo se montaba con CUATRO trozos pegados, dos puntos y espacio
          final incluidos. Ahora es una frase entera con dos huecos: en frances
          los dos puntos llevan su propio espacio y el orden no es este.
        */}
        <div className="step-indicator">
          <span className="step-badge">
            {t("common:asistente.pasoDe", {
              actual: currentStep + 1,
              total: TOTAL_PASOS,
            })}
          </span>
          <h3>
            {t(
              isEditing
                ? "asistenteTerapia.tituloEditar"
                : "asistenteTerapia.tituloNuevo",
              { paso: t(`asistenteTerapia.pasos.${PASOS[currentStep]}`) },
            )}
          </h3>
        </div>

        <Steps
          current={currentStep}
          size="small"
          className="mini-steps"
          items={stepsItems}
        />
      </div>

      {/* Contenido Dinámico */}
      <div className="wizard-body">{renderContent()}</div>

      {/* Footer de Navegación */}
      <div className="wizard-footer">
        <Button onClick={currentStep === 0 ? onClose : prev} disabled={loading}>
          {currentStep === 0 ? t("common:actions.cancel") : t("acciones.atras")}
        </Button>

        <Button
          type="primary"
          onClick={currentStep === 2 ? handleSubmit : next}
          disabled={!canProceed()}
          loading={loading}
        >
          {currentStep === 2
            ? isEditing
              ? t("common:actions.saveChanges")
              : t("asistenteTerapia.crear")
            : t("asistenteTerapia.siguientePaso")}
        </Button>
      </div>
    </Modal>
  );
}
